import * as React from 'react';
import { AppContext } from '../../../App';
import { useQueryClient } from '@tanstack/react-query';
import { Box, CircularProgress, Typography } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

import { ITrainingSet } from '../../../types/my-types';
import { ApiError } from '../../../api/types';
import { useTrainingSetCertify } from '../../hooks/useTrainingSetCertify';
import { useTrainingSetRevokeCertification } from '../../hooks/useTrainingSetRevokeCertification';
import { getValidationType } from '../../../utils/validationUtils';

import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import ValidationErrorsTable, { ValidationErrorRow } from '../../common/ValidationErrorsTable';
import { CERTIFIED_BLUE } from '../../common/CertifiedBadge';

interface ICertifyBlockingError {
  document_id: string;
  label?: string;
  error?: string | null;
  error_detail?: string | null;
}

// The API returns every failing field on every document, uncapped. A broadly
// applied rule failing across a large set can run to thousands of entries, and
// the table renders a row each. Enough rows to act on, then a count.
const MAX_BLOCKING_ERRORS_SHOWN = 100;

export interface ITrainingSetCertifyProps {
  trainingSet: ITrainingSet;
  dpid: string;
}

/**
 * Reads the per-document errors that the certify endpoint returns with a 400.
 * Returns an empty array for any other failure, which the caller reports as a
 * plain error toast instead.
 */
const readBlockingErrors = (error: unknown): ICertifyBlockingError[] => {
  const body = (error as ApiError)?.responseData as
    | { errors?: ICertifyBlockingError[] }
    | undefined;

  return Array.isArray(body?.errors) ? body!.errors : [];
};

const TrainingSetCertify: React.FC<ITrainingSetCertifyProps> = ({ trainingSet, dpid }) => {
  const { setToast } = React.useContext(AppContext);
  const [blockingErrors, setBlockingErrors] = React.useState<ICertifyBlockingError[]>([]);

  const client = useQueryClient();
  const trainingSetId = trainingSet.id;
  const isCertified = !!trainingSet.certified_at;

  // An edit withdraws certification server-side, so the badge can vanish
  // without anyone pressing this button. State that as the outcome of the edit
  // rather than leaving the operator to read it as a bug.
  const previousCertifiedAt = React.useRef(trainingSet.certified_at);
  const revokedHere = React.useRef(false);
  // Keyed to the certification it reported on, not to a transition. An
  // in-flight refetch can land the old certified_at back in the cache after an
  // edit already cleared it, so the same certification can appear to lapse more
  // than once. Only a genuinely new certification re-arms this.
  const reportedFor = React.useRef<string | null | undefined>(undefined);

  const invalidate = () => {
    client.invalidateQueries({ queryKey: ['trainingSets'] });
    client.invalidateQueries({ queryKey: ['trainingSet', trainingSetId] });
  };

  const certify = useTrainingSetCertify({
    onSuccess: () => {
      invalidate();
      setToast({
        variant: 'success',
        open: true,
        message: 'Training set certified.',
      });
    },
    onError: (error) => {
      const errors = readBlockingErrors(error);
      if (errors.length > 0) {
        setBlockingErrors(errors);
        return;
      }

      setToast({
        variant: 'error',
        open: true,
        message: `Sorry, failed to certify this training set. ${error}`,
      });
    },
  });

  const revokeCertification = useTrainingSetRevokeCertification({
    onSuccess: () => {
      revokedHere.current = true;
      invalidate();
      setToast({
        variant: 'success',
        open: true,
        message: 'Certification removed.',
      });
    },
    onError: (error) => {
      setToast({
        variant: 'error',
        open: true,
        message: `Sorry, failed to remove the certification. ${error}`,
      });
    },
  });

  const isPending = certify.isPending || revokeCertification.isPending;

  React.useEffect(() => {
    const previousCertification = previousCertifiedAt.current;
    const wasCertified = !!previousCertification;
    previousCertifiedAt.current = trainingSet.certified_at;

    if (isCertified || !wasCertified) {
      return;
    }

    if (reportedFor.current === previousCertification) {
      return;
    }

    reportedFor.current = previousCertification;

    if (revokedHere.current) {
      revokedHere.current = false;
      return;
    }

    // Follows the edit's own "saved" toast and replaces it. That this appears
    // at all says the edit landed, so it only has to carry the consequence.
    setToast({
      variant: 'warning',
      open: true,
      message: 'Certification revoked — re-certify when the set is ready.',
    });
  }, [trainingSet.certified_at, isCertified, setToast]);

  const handleClick = () => {
    if (isCertified) {
      revokeCertification.mutate({ trainingSetId });
    } else {
      certify.mutate({ trainingSetId });
    }
  };

  const errorRows: ValidationErrorRow[] = blockingErrors
    .slice(0, MAX_BLOCKING_ERRORS_SHOWN)
    .map((e) => ({
      entityId: e.document_id,
      field: e.label || '',
      type: getValidationType({ label: e.label, error: e.error, error_detail: e.error_detail }),
      error: e.error || null,
      errorDetail: e.error_detail || null,
    }));

  const hiddenErrorCount = blockingErrors.length - errorRows.length;

  const closeBlockingErrors = () => setBlockingErrors([]);

  return (
    <>
      <IconButtonWhiteBg
        disabled={isPending}
        onClick={handleClick}
        id="icon-training-set-certify"
        cypressId="icon-training-set-certify"
        tooltip={isCertified ? 'Remove certification' : 'Certify training set'}
      >
        {isPending ? (
          <CircularProgress size={14} />
        ) : isCertified ? (
          <VerifiedIcon style={{ fontSize: '14px', color: CERTIFIED_BLUE }} />
        ) : (
          <VerifiedOutlinedIcon style={{ fontSize: '14px', color: 'black' }} />
        )}
      </IconButtonWhiteBg>

      <DialogGlynt
        open={blockingErrors.length > 0}
        closeModal={closeBlockingErrors}
        cypressId="dialog-training-set-certify-errors"
        width="800px"
      >
        <DialogTitleGlynt
          title="Cannot certify — fix these first"
          closeModal={closeBlockingErrors}
        />
        <Box sx={{ p: 2, flex: 1, maxHeight: '60vh', overflow: 'auto' }}>
          <ValidationErrorsTable
            errors={errorRows}
            entityIdLabel="Document ID"
            getEntityLink={(documentId) =>
              `/data-pools/${dpid}/train/${trainingSetId}/mark/${documentId}`
            }
          />
          {hiddenErrorCount > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
              data-cy="lbl-certify-errors-truncated"
            >
              Showing {errorRows.length} of {blockingErrors.length}. Fix these and try again to
              see the rest.
            </Typography>
          )}
        </Box>
        <DialogActionsGlynt
          handleSubmit={closeBlockingErrors}
          closeModal={closeBlockingErrors}
          cancelCypressId="btn-training-set-certify-errors-cancel"
          submitCypressId="btn-training-set-certify-errors-close"
          clearCypressId=""
          submitButtonText="Close"
          submitLoading={false}
          showClearButton={false}
          showCancelButton={false}
        />
      </DialogGlynt>
    </>
  );
};

export default TrainingSetCertify;
